import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLeads, createLead, updateLead, deleteLead, leadZuAnfrage, leadZuKunde,
  getAnfragen, createAnfrage, updateAnfrage, deleteAnfrage, anfrageZuKunde,
  CrmLead, LeadStatus, CrmAnfrage, AnfrageStatus
} from '@/lib/crm-api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { openPotenzialBerichtPdf, sendePotenzialBericht, getPotenzialAdmin, setzePotenzialFollowup, getBetriebe } from '@/lib/analytics-api';

const leadStatusLabels: Record<LeadStatus, string> = {
  'neu': 'Nicht kontaktiert',
  'kontaktiert': 'Kontaktiert',
  'erstgespraech_geplant': 'Erstgespräch geplant',
  'interessiert': 'Interessiert',
  'nicht_interessiert': 'Nicht interessiert',
  'spaeter': 'Später',
  'gewonnen': 'Gewonnen',
};

const anfrageStatusLabels: Record<AnfrageStatus, string> = {
  'neu': 'Neu eingegangen',
  'erstgespraech_geplant': 'Erstgespräch',
  'angebot_in_arbeit': 'Angebot erstellen',
  'angebot_versendet': 'Angebot versendet',
  'verkaufsgespraech': 'Verkaufsgespräch',
  'nachfassen': 'Nachfassen',
  'gewonnen': 'Gewonnen',
  'verloren': 'Verloren',
};

const quelleOptions = [
  { value: '', label: 'Bitte wählen' },
  { value: 'Website Kontaktformular', label: 'Website Kontaktformular' },
  { value: 'Kontaktformular', label: 'Kontaktformular (Sonstige)' },
  { value: 'Anruf', label: 'Anruf' },
  { value: 'Empfehlung', label: 'Empfehlung' },
  { value: 'Google Ads', label: 'Google Ads' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Messe', label: 'Messe' },
  { value: 'Sonstiges', label: 'Sonstiges' },
];

export default function CRMPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'kaltakquise' | 'anfragen' | 'betriebscheck' | 'potenzial'>('anfragen');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<'lead' | 'anfrage'>('anfrage');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedAnfrageId, setSelectedAnfrageId] = useState<string | null>(null);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);

  const { data: leads = [], isLoading: loadingLeads } = useQuery({ queryKey: ['crm-leads'], queryFn: getLeads });
  const { data: anfragen = [], isLoading: loadingAnfragen } = useQuery({ queryKey: ['crm-anfragen'], queryFn: getAnfragen });
  // Kaltakquise lebt seit 06.09.2026 unter „Betriebe" (Master-CRM): hier nur noch
  // der Zähler der geprüften, noch nicht angerufenen Kandidaten (Status „neu").
  const { data: kandidaten = [] } = useQuery({ queryKey: ['betriebe', 'kandidaten'], queryFn: () => getBetriebe({ status: 'neu' }), staleTime: 60_000 });

  const createLeadMut = useMutation({
    mutationFn: createLead,
    onSuccess: (newLead) => {
      queryClient.invalidateQueries({ queryKey: ['crm-leads'] });
      setShowCreateModal(false);
      setSelectedLeadId(newLead.id);
      toast.success('Kaltakquise-Eintrag erstellt');
    },
  });

  const updateLeadMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CrmLead> }) => {
      console.log('🔵 Updating Lead:', id, data);
      return updateLead(id, data);
    },
    onSuccess: () => {
      console.log('✅ Lead updated successfully');
      queryClient.invalidateQueries({ queryKey: ['crm-leads'] });
      toast.success('Gespeichert');
    },
    onError: (error) => {
      console.error('❌ Error updating lead:', error);
      toast.error('Fehler beim Speichern: ' + (error as Error).message);
    },
  });

  const deleteLeadMut = useMutation({
    mutationFn: deleteLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-leads'] });
      setSelectedLeadId(null);
      toast.success('Gelöscht');
    },
  });

  const leadZuAnfrageMut = useMutation({
    mutationFn: leadZuAnfrage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-leads', 'crm-anfragen'] });
      setSelectedLeadId(null);
      toast.success('Zu Anfrage konvertiert');
      setActiveTab('anfragen');
    },
  });

  const leadZuKundeMut = useMutation({
    mutationFn: leadZuKunde,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-leads'] });
      setSelectedLeadId(null);
      toast.success('Kunde angelegt');
      navigate('/todos');
    },
  });

  const createAnfrageMut = useMutation({
    mutationFn: createAnfrage,
    onSuccess: (newAnfrage) => {
      queryClient.invalidateQueries({ queryKey: ['crm-anfragen'] });
      setShowCreateModal(false);
      setSelectedAnfrageId(newAnfrage.id);
      toast.success('Anfrage erstellt');
    },
  });

  const updateAnfrageMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CrmAnfrage> }) => {
      console.log('🔵 Updating Anfrage:', id, data);
      return updateAnfrage(id, data);
    },
    onSuccess: () => {
      console.log('✅ Anfrage updated successfully');
      queryClient.invalidateQueries({ queryKey: ['crm-anfragen'] });
      toast.success('Gespeichert');
    },
    onError: (error) => {
      console.error('❌ Error updating anfrage:', error);
      toast.error('Fehler beim Speichern: ' + (error as Error).message);
    },
  });

  const deleteAnfrageMut = useMutation({
    mutationFn: deleteAnfrage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-anfragen'] });
      setSelectedAnfrageId(null);
      toast.success('Gelöscht');
    },
  });

  const anfrageZuKundeMut = useMutation({
    mutationFn: anfrageZuKunde,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-anfragen'] });
      setSelectedAnfrageId(null);
      toast.success('Kunde angelegt');
      navigate('/todos');
    },
  });

  const heute = new Date().toISOString().split('T')[0];
  const aktiveLeads = leads.filter(l => !['nicht_interessiert', 'spaeter', 'gewonnen'].includes(l.status));
  const alleBetriebschecks = anfragen.filter(a => a.quelle === 'Digitaler Betriebscheck');
  const allePotenzialChecks = anfragen.filter(a => a.quelle === 'Potenzial-Check');
  const aktiveAnfragen = anfragen.filter(a => !['gewonnen', 'verloren'].includes(a.status) && a.quelle !== 'Digitaler Betriebscheck' && a.quelle !== 'Potenzial-Check');
  const aktiveBetriebschecks = alleBetriebschecks.filter(a => !['gewonnen', 'verloren'].includes(a.status));
  const aktivePotenzialChecks = allePotenzialChecks.filter(a => !['gewonnen', 'verloren'].includes(a.status));

  const selectedLead = leads.find(l => l.id === selectedLeadId);
  const selectedAnfrage = anfragen.find(a => a.id === selectedAnfrageId);

  if (loadingLeads || loadingAnfragen) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">CRM</h1>
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => { setActiveTab('kaltakquise'); setSelectedLeadId(null); setSelectedAnfrageId(null); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'kaltakquise' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500'
              }`}
            >
              Kaltakquise ({kandidaten.length})
            </button>
            <button
              onClick={() => { setActiveTab('anfragen'); setSelectedLeadId(null); setSelectedAnfrageId(null); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'anfragen' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500'
              }`}
            >
              Anfragen ({aktiveAnfragen.length})
            </button>
            <button
              onClick={() => { setActiveTab('betriebscheck'); setSelectedLeadId(null); setSelectedAnfrageId(null); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'betriebscheck' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500'
              }`}
            >
              Betriebscheck ({aktiveBetriebschecks.length})
            </button>
            <button
              onClick={() => { setActiveTab('potenzial'); setSelectedLeadId(null); setSelectedAnfrageId(null); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === 'potenzial' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500'
              }`}
            >
              Potenzial-Checks ({aktivePotenzialChecks.length})
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowIntegrationModal(true)}>
            Integration
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>Neu</Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex gap-6 min-h-0">
        {/* Liste */}
        <div className="flex-1 min-w-0">
          {activeTab === 'kaltakquise' && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-900">Kaltakquise läuft jetzt über „Betriebe"</p>
                <p className="mt-1 text-sm text-gray-500 max-w-2xl">
                  Ein Betrieb, darunter je Produkt eine Chance mit eigener Phase — Publivo, Website, Google Ads, Recruiting.
                  Die Anrufliste, die Prüfung und das Analyse-PDF liegen dort.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div>
                    <div className="text-2xl font-semibold text-gray-900 tabular-nums">{kandidaten.length}</div>
                    <div className="text-xs text-gray-500">geprüfte Betriebe, noch nicht angerufen</div>
                  </div>
                  <Button onClick={() => navigate('/betriebe')}>Zu Betriebe</Button>
                </div>
              </div>
              {/* Alte Kaltakquise-Einträge (crm_leads) bleiben erreichbar, bis sie abgearbeitet sind */}
              {aktiveLeads.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Bisherige Einträge ({aktiveLeads.length})</p>
                  <LeadsTable leads={aktiveLeads} selectedId={selectedLeadId} onSelect={setSelectedLeadId} heute={heute} />
                </div>
              )}
            </div>
          )}
          {activeTab === 'anfragen' && (
            <AnfragenTable anfragen={aktiveAnfragen} selectedId={selectedAnfrageId} onSelect={setSelectedAnfrageId} heute={heute} />
          )}
          {activeTab === 'betriebscheck' && (
            <BetriebscheckTable anfragen={aktiveBetriebschecks} selectedId={selectedAnfrageId} onSelect={setSelectedAnfrageId} />
          )}
          {activeTab === 'potenzial' && (
            <PotenzialCheckTable anfragen={aktivePotenzialChecks} selectedId={selectedAnfrageId} onSelect={setSelectedAnfrageId} />
          )}
        </div>

        {/* Detail Panel */}
        <div className="w-96 flex-shrink-0">
          {activeTab === 'kaltakquise' ? (
            selectedLead ? (
              <LeadDetailPanel
                lead={selectedLead}
                onUpdate={(data) => updateLeadMut.mutate({ id: selectedLead.id, data })}
                onDelete={() => { if (confirm('Löschen?')) deleteLeadMut.mutate(selectedLead.id); }}
                onToAnfrage={() => { if (confirm('Zu Anfrage?')) leadZuAnfrageMut.mutate(selectedLead.id); }}
                onToKunde={() => { if (confirm('Zu Kunde?')) leadZuKundeMut.mutate(selectedLead.id); }}
                onClose={() => setSelectedLeadId(null)}
              />
            ) : (
              <EmptyPanel />
            )
          ) : activeTab === 'potenzial' ? (
            selectedAnfrage ? (
              <PotenzialCheckDetailPanel
                anfrage={selectedAnfrage}
                onUpdate={(data) => updateAnfrageMut.mutate({ id: selectedAnfrage.id, data })}
                onDelete={() => { if (confirm('Löschen?')) deleteAnfrageMut.mutate(selectedAnfrage.id); }}
                onToKunde={() => { if (confirm('Gewonnen!')) anfrageZuKundeMut.mutate(selectedAnfrage.id); }}
                onClose={() => setSelectedAnfrageId(null)}
              />
            ) : (
              <EmptyPanel />
            )
          ) : activeTab === 'betriebscheck' ? (
            selectedAnfrage ? (
              <BetriebscheckDetailPanel
                anfrage={selectedAnfrage}
                onUpdate={(data) => updateAnfrageMut.mutate({ id: selectedAnfrage.id, data })}
                onDelete={() => { if (confirm('Löschen?')) deleteAnfrageMut.mutate(selectedAnfrage.id); }}
                onToKunde={() => { if (confirm('Gewonnen!')) anfrageZuKundeMut.mutate(selectedAnfrage.id); }}
                onClose={() => setSelectedAnfrageId(null)}
              />
            ) : (
              <EmptyPanel />
            )
          ) : (
            selectedAnfrage ? (
              <AnfrageDetailPanel
                anfrage={selectedAnfrage}
                onUpdate={(data) => updateAnfrageMut.mutate({ id: selectedAnfrage.id, data })}
                onDelete={() => { if (confirm('Löschen?')) deleteAnfrageMut.mutate(selectedAnfrage.id); }}
                onToKunde={() => { if (confirm('Gewonnen!')) anfrageZuKundeMut.mutate(selectedAnfrage.id); }}
                onClose={() => setSelectedAnfrageId(null)}
              />
            ) : (
              <EmptyPanel />
            )
          )}
        </div>
      </div>

      {/* Integration Modal */}
      {showIntegrationModal && (
        <Modal isOpen onClose={() => setShowIntegrationModal(false)} title="Website-Integration" size="lg">
          <IntegrationContent />
        </Modal>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal isOpen onClose={() => setShowCreateModal(false)} title="Neuer Eintrag" size="lg">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Typ wählen</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setCreateType('anfrage')}
                  className={`p-3 rounded border text-sm text-left ${createType === 'anfrage' ? 'border-gray-900 bg-gray-50' : 'border-gray-200'}`}
                >
                  <p className="font-medium">Anfrage</p>
                  <p className="text-xs text-gray-500">Formular/Anruf/Empfehlung</p>
                </button>
                <button
                  onClick={() => setCreateType('lead')}
                  className={`p-3 rounded border text-sm text-left ${createType === 'lead' ? 'border-gray-900 bg-gray-50' : 'border-gray-200'}`}
                >
                  <p className="font-medium">Kaltakquise</p>
                  <p className="text-xs text-gray-500">Potenzielle Kunden</p>
                </button>
              </div>
            </div>

            {createType === 'lead' ? (
              <LeadForm onSubmit={(data) => createLeadMut.mutate(data)} onCancel={() => setShowCreateModal(false)} isLoading={createLeadMut.isPending} />
            ) : (
              <AnfrageForm onSubmit={(data) => createAnfrageMut.mutate(data)} onCancel={() => setShowCreateModal(false)} isLoading={createAnfrageMut.isPending} />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function LeadsTable({ leads, selectedId, onSelect, heute }: {
  leads: CrmLead[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  heute: string;
}) {
  if (leads.length === 0) {
    return <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500">Keine Einträge</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden h-full flex flex-col">
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Firma</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Ansprechpartner</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Kontakt</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Nächster Termin</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {leads.map(lead => {
              const istFaellig = lead.naechster_kontakt && lead.naechster_kontakt <= heute;
              const isSelected = lead.id === selectedId;

              return (
                <tr
                  key={lead.id}
                  onClick={() => onSelect(lead.id)}
                  className={`cursor-pointer transition ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{lead.firma}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{leadStatusLabels[lead.status]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{lead.ansprechpartner || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-600">
                      {lead.email && <div className="truncate max-w-[200px]">{lead.email}</div>}
                      {lead.telefon && <div>{lead.telefon}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {lead.naechster_kontakt ? (
                      <span className={`text-xs ${istFaellig ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(lead.naechster_kontakt).toLocaleDateString('de-DE')}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnfragenTable({ anfragen, selectedId, onSelect, heute }: {
  anfragen: CrmAnfrage[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  heute: string;
}) {
  if (anfragen.length === 0) {
    return <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500">Keine Einträge</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden h-full flex flex-col">
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Firma</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Quelle</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Position</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Herausforderung</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Ansprechpartner</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Kontakt</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Nachfassen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {anfragen.map(anfrage => {
              const istNachfass = anfrage.nachfass_datum && anfrage.nachfass_datum <= heute;
              const isSelected = anfrage.id === selectedId;

              return (
                <tr
                  key={anfrage.id}
                  onClick={() => onSelect(anfrage.id)}
                  className={`cursor-pointer transition ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{anfrage.firma}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{anfrageStatusLabels[anfrage.status]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{anfrage.quelle || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{anfrage.position || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{anfrage.herausforderung || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{anfrage.ansprechpartner || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-600">
                      {anfrage.email && <div className="truncate max-w-[200px]">{anfrage.email}</div>}
                      {anfrage.telefon && <div>{anfrage.telefon}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {anfrage.nachfass_datum ? (
                      <span className={`text-xs ${istNachfass ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {new Date(anfrage.nachfass_datum).toLocaleDateString('de-DE')}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyPanel() {
  return (
    <div className="bg-white border border-gray-200 rounded p-8 text-center h-full flex items-center justify-center">
      <p className="text-sm text-gray-400">Wähle einen Eintrag</p>
    </div>
  );
}

function LeadDetailPanel({ lead, onUpdate, onDelete, onToAnfrage, onToKunde, onClose }: {
  lead: CrmLead;
  onUpdate: (data: Partial<CrmLead>) => void;
  onDelete: () => void;
  onToAnfrage: () => void;
  onToKunde: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    firma: lead.firma,
    status: lead.status,
    ansprechpartner: lead.ansprechpartner || '',
    email: lead.email || '',
    telefon: lead.telefon || '',
    branche: lead.branche || '',
    website: lead.website || '',
    naechster_kontakt: lead.naechster_kontakt || '',
    notizen: lead.notizen || '',
  });

  // Update form when lead changes
  useEffect(() => {
    setForm({
      firma: lead.firma,
      status: lead.status,
      ansprechpartner: lead.ansprechpartner || '',
      email: lead.email || '',
      telefon: lead.telefon || '',
      branche: lead.branche || '',
      website: lead.website || '',
      naechster_kontakt: lead.naechster_kontakt || '',
      notizen: lead.notizen || '',
    });
  }, [lead]);

  const handleSave = () => {
    console.log('🟢 LeadDetailPanel handleSave called', form);
    onUpdate({
      firma: form.firma,
      status: form.status,
      ansprechpartner: form.ansprechpartner || null,
      email: form.email || null,
      telefon: form.telefon || null,
      branche: form.branche || null,
      website: form.website || null,
      naechster_kontakt: form.naechster_kontakt || null,
      notizen: form.notizen || null,
    });
  };

  const leadStatusOpts = Object.entries(leadStatusLabels).map(([k, v]) => ({ value: k, label: v }));

  return (
    <div className="bg-white border border-gray-200 rounded h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <span className="text-sm font-medium text-gray-900">Details</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3">
        <Input label="Firma" value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} />
        <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as LeadStatus })} options={leadStatusOpts} />
        <Input label="Ansprechpartner" value={form.ansprechpartner} onChange={(e) => setForm({ ...form, ansprechpartner: e.target.value })} />
        <Input label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
        <Input label="Branche" value={form.branche} onChange={(e) => setForm({ ...form, branche: e.target.value })} />
        <Input label="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        <Input label="Nächster Kontakt" type="date" value={form.naechster_kontakt} onChange={(e) => setForm({ ...form, naechster_kontakt: e.target.value })} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
            rows={3}
            value={form.notizen}
            onChange={(e) => setForm({ ...form, notizen: e.target.value })}
          />
        </div>
      </div>

      <div className="p-4 border-t border-gray-200 space-y-2 flex-shrink-0">
        <Button onClick={handleSave} className="w-full">Speichern</Button>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onToAnfrage} className="py-2 text-xs text-gray-600 hover:bg-gray-50 rounded border border-gray-200">Zu Anfrage</button>
          <button onClick={onToKunde} className="py-2 text-xs text-gray-600 hover:bg-gray-50 rounded border border-gray-200">Zu Kunde</button>
        </div>
        <button onClick={onDelete} className="w-full py-2 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200">Löschen</button>
      </div>
    </div>
  );
}

function AnfrageDetailPanel({ anfrage, onUpdate, onDelete, onToKunde, onClose }: {
  anfrage: CrmAnfrage;
  onUpdate: (data: Partial<CrmAnfrage>) => void;
  onDelete: () => void;
  onToKunde: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    firma: anfrage.firma,
    status: anfrage.status,
    ansprechpartner: anfrage.ansprechpartner || '',
    email: anfrage.email || '',
    telefon: anfrage.telefon || '',
    quelle: anfrage.quelle || '',
    position: anfrage.position || '',
    herausforderung: anfrage.herausforderung || '',
    interesse: anfrage.interesse || '',
    angebot_datum: anfrage.angebot_datum || '',
    nachfass_datum: anfrage.nachfass_datum || '',
    notizen: anfrage.notizen || '',
  });

  // Update form when anfrage changes
  useEffect(() => {
    setForm({
      firma: anfrage.firma,
      status: anfrage.status,
      ansprechpartner: anfrage.ansprechpartner || '',
      email: anfrage.email || '',
      telefon: anfrage.telefon || '',
      quelle: anfrage.quelle || '',
      position: anfrage.position || '',
      herausforderung: anfrage.herausforderung || '',
      interesse: anfrage.interesse || '',
      angebot_datum: anfrage.angebot_datum || '',
      nachfass_datum: anfrage.nachfass_datum || '',
      notizen: anfrage.notizen || '',
    });
  }, [anfrage]);

  const handleSave = () => {
    console.log('🟢 AnfrageDetailPanel handleSave called', form);
    onUpdate({
      firma: form.firma,
      status: form.status,
      ansprechpartner: form.ansprechpartner || null,
      email: form.email || null,
      telefon: form.telefon || null,
      quelle: form.quelle || null,
      position: form.position || null,
      herausforderung: form.herausforderung || null,
      interesse: form.interesse || null,
      angebot_datum: form.angebot_datum || null,
      nachfass_datum: form.nachfass_datum || null,
      notizen: form.notizen || null,
    });
  };

  const anfrageStatusOpts = Object.entries(anfrageStatusLabels).map(([k, v]) => ({ value: k, label: v }));
  
  const positionOptions = [
    { value: '', label: 'Bitte wählen' },
    { value: 'Inhaber / Geschäftsführer', label: 'Inhaber / Geschäftsführer' },
    { value: 'Abteilungsleiter', label: 'Abteilungsleiter' },
    { value: 'Marketing / HR', label: 'Marketing / HR' },
    { value: 'Sonstige', label: 'Sonstige' },
  ];
  
  const herausforderungOptions = [
    { value: '', label: 'Bitte wählen' },
    { value: 'Zu wenig Fachkräfte', label: 'Zu wenig Fachkräfte' },
    { value: 'Zu wenig Planbarkeit (Aufträge)', label: 'Zu wenig Planbarkeit (Aufträge)' },
    { value: 'Chaos im Büro / Prozesse', label: 'Chaos im Büro / Prozesse' },
    { value: 'Veralteter Außenauftritt', label: 'Veralteter Außenauftritt' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <span className="text-sm font-medium text-gray-900">Details</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3">
        <Input label="Firma" value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} />
        <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as AnfrageStatus })} options={anfrageStatusOpts} />
        <Input label="Ansprechpartner" value={form.ansprechpartner} onChange={(e) => setForm({ ...form, ansprechpartner: e.target.value })} />
        <Select label="Quelle" value={form.quelle} onChange={(e) => setForm({ ...form, quelle: e.target.value })} options={quelleOptions} />
        <Select label="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} options={positionOptions} />
        <Select label="Herausforderung" value={form.herausforderung} onChange={(e) => setForm({ ...form, herausforderung: e.target.value })} options={herausforderungOptions} />
        <Input label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Interesse / Bedarf</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
            rows={2}
            value={form.interesse}
            onChange={(e) => setForm({ ...form, interesse: e.target.value })}
          />
        </div>
        <Input label="Angebot erstellt" type="date" value={form.angebot_datum} onChange={(e) => setForm({ ...form, angebot_datum: e.target.value })} />
        <Input label="Nachfassen am" type="date" value={form.nachfass_datum} onChange={(e) => setForm({ ...form, nachfass_datum: e.target.value })} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
            rows={3}
            value={form.notizen}
            onChange={(e) => setForm({ ...form, notizen: e.target.value })}
          />
        </div>
      </div>

      <div className="p-4 border-t border-gray-200 space-y-2 flex-shrink-0">
        <Button onClick={handleSave} className="w-full">Speichern</Button>
        <button onClick={onToKunde} className="w-full py-2 text-xs text-green-600 hover:bg-green-50 rounded border border-green-200 font-medium">Gewonnen → Kunde</button>
        <button onClick={onDelete} className="w-full py-2 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200">Löschen</button>
      </div>
    </div>
  );
}

function LeadForm({ onSubmit, onCancel, isLoading }: {
  onSubmit: (data: Partial<CrmLead>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState({ firma: '', ansprechpartner: '', email: '', telefon: '', branche: '', naechster_kontakt: '' });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-3">
      <Input label="Firma" value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} required />
      <Input label="Ansprechpartner" value={form.ansprechpartner} onChange={(e) => setForm({ ...form, ansprechpartner: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <Input label="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
      </div>
      <Input label="Branche" value={form.branche} onChange={(e) => setForm({ ...form, branche: e.target.value })} />
      <Input label="Nächster Kontakt" type="date" value={form.naechster_kontakt} onChange={(e) => setForm({ ...form, naechster_kontakt: e.target.value })} />
      <div className="flex justify-end gap-2 pt-3">
        <Button type="button" variant="secondary" onClick={onCancel}>Abbrechen</Button>
        <Button type="submit" disabled={isLoading}>Erstellen</Button>
      </div>
    </form>
  );
}

function AnfrageForm({ onSubmit, onCancel, isLoading }: {
  onSubmit: (data: Partial<CrmAnfrage>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState({ 
    firma: '', 
    ansprechpartner: '', 
    email: '', 
    telefon: '', 
    quelle: '', 
    position: '', 
    herausforderung: '', 
    interesse: '' 
  });

  const positionOpts = [
    { value: '', label: 'Bitte wählen' },
    { value: 'Inhaber / Geschäftsführer', label: 'Inhaber / Geschäftsführer' },
    { value: 'Abteilungsleiter', label: 'Abteilungsleiter' },
    { value: 'Marketing / HR', label: 'Marketing / HR' },
    { value: 'Sonstige', label: 'Sonstige' },
  ];

  const herausforderungOpts = [
    { value: '', label: 'Bitte wählen' },
    { value: 'Zu wenig Fachkräfte', label: 'Zu wenig Fachkräfte' },
    { value: 'Zu wenig Planbarkeit (Aufträge)', label: 'Zu wenig Planbarkeit (Aufträge)' },
    { value: 'Chaos im Büro / Prozesse', label: 'Chaos im Büro / Prozesse' },
    { value: 'Veralteter Außenauftritt', label: 'Veralteter Außenauftritt' },
  ];

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-3">
      <Input label="Firma" value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} required />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Ansprechpartner" value={form.ansprechpartner} onChange={(e) => setForm({ ...form, ansprechpartner: e.target.value })} />
        <Select label="Quelle" value={form.quelle} onChange={(e) => setForm({ ...form, quelle: e.target.value })} options={quelleOptions} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} options={positionOpts} />
        <Select label="Herausforderung" value={form.herausforderung} onChange={(e) => setForm({ ...form, herausforderung: e.target.value })} options={herausforderungOpts} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Interesse / Bedarf</label>
        <textarea
          className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
          rows={2}
          value={form.interesse}
          onChange={(e) => setForm({ ...form, interesse: e.target.value })}
        />
      </div>
      <div className="flex justify-end gap-2 pt-3">
        <Button type="button" variant="secondary" onClick={onCancel}>Abbrechen</Button>
        <Button type="submit" disabled={isLoading}>Erstellen</Button>
      </div>
    </form>
  );
}

interface BetriebscheckData {
  score?: number;
  score_label?: string;
  einstiegsDaten?: {
    branche?: string;
    mitarbeiter?: string;
    herausforderungen?: string[];
    details?: Record<string, string[]>;
    digitalisierung?: string;
    zeitaufwand?: string;
  };
  gespraechsVerlauf?: Array<{ frage: string; antwort: string; kernthema: string }>;
  analyse?: {
    betrieb?: { name_branche: string; zusammenfassung: string };
    gesamt_score: number;
    score_label: string;
    scores_nach_bereich?: Record<string, { score: number; label: string }>;
    staerken?: Array<{ titel: string; beschreibung: string }>;
    blind_spots?: Array<{ titel: string; was_passiert_gerade: string; warum_problematisch: string; zeitverlust_pro_woche: string }>;
    top_schwachstellen?: Array<{ rang: number; titel: string; beschreibung: string; zeitverlust_pro_woche: string; geschaeftsrisiko: string }>;
    automatisierungspotenziale?: Array<{ prozess: string; aktueller_aufwand: string; potenzial: string; zeitersparnis_pro_woche: string }>;
    gesamtfazit?: string;
    naechster_schritt?: string;
  };
}

function parseBetriebscheckNotizen(notizen: string | null): BetriebscheckData | null {
  if (!notizen) return null;
  try {
    const data = JSON.parse(notizen);
    if (data.type === 'betriebscheck') return data;
  } catch { /* not JSON */ }
  return null;
}

function BetriebscheckTable({ anfragen, selectedId, onSelect }: {
  anfragen: CrmAnfrage[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (anfragen.length === 0) {
    return <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500">Keine Betriebschecks vorhanden</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden h-full flex flex-col">
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Firma</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Score</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Ansprechpartner</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Kontakt</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Fragen</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Datum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {anfragen.map(anfrage => {
              const isSelected = anfrage.id === selectedId;
              const bcData = parseBetriebscheckNotizen(anfrage.notizen);
              const scoreText = anfrage.interesse || '—';
              const fragenCount = bcData?.gespraechsVerlauf?.length || 0;

              return (
                <tr
                  key={anfrage.id}
                  onClick={() => onSelect(anfrage.id)}
                  className={`cursor-pointer transition ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{anfrage.firma || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    {bcData?.score != null ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        bcData.score >= 60 ? 'bg-green-50 text-green-700' :
                        bcData.score >= 35 ? 'bg-yellow-50 text-yellow-700' :
                        'bg-red-50 text-red-700'
                      }`}>
                        {bcData.score}/100
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">{scoreText}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{anfrageStatusLabels[anfrage.status]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{anfrage.ansprechpartner || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-600">
                      {anfrage.email && <div className="truncate max-w-[180px]">{anfrage.email}</div>}
                      {anfrage.telefon && <div>{anfrage.telefon}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{fragenCount > 0 ? `${fragenCount} Fragen` : '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">
                      {new Date(anfrage.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 text-left transition"
      >
        <span className="text-xs font-medium text-gray-700">{title}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-3 py-3 text-xs space-y-2">{children}</div>}
    </div>
  );
}

function BetriebscheckDetailPanel({ anfrage, onUpdate, onDelete, onToKunde, onClose }: {
  anfrage: CrmAnfrage;
  onUpdate: (data: Partial<CrmAnfrage>) => void;
  onDelete: () => void;
  onToKunde: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    firma: anfrage.firma,
    status: anfrage.status,
    ansprechpartner: anfrage.ansprechpartner || '',
    email: anfrage.email || '',
    telefon: anfrage.telefon || '',
    nachfass_datum: anfrage.nachfass_datum || '',
  });

  useEffect(() => {
    setForm({
      firma: anfrage.firma,
      status: anfrage.status,
      ansprechpartner: anfrage.ansprechpartner || '',
      email: anfrage.email || '',
      telefon: anfrage.telefon || '',
      nachfass_datum: anfrage.nachfass_datum || '',
    });
  }, [anfrage]);

  const handleSave = () => {
    onUpdate({
      firma: form.firma,
      status: form.status,
      ansprechpartner: form.ansprechpartner || null,
      email: form.email || null,
      telefon: form.telefon || null,
      nachfass_datum: form.nachfass_datum || null,
    });
  };

  const bcData = parseBetriebscheckNotizen(anfrage.notizen);
  const anfrageStatusOpts = Object.entries(anfrageStatusLabels).map(([k, v]) => ({ value: k, label: v }));

  return (
    <div className="bg-white border border-gray-200 rounded h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <span className="text-sm font-medium text-gray-900">Betriebscheck Details</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3">
        {/* Score Badge */}
        {bcData?.score != null && (
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-teal-700">{bcData.score}<span className="text-lg text-teal-500">/100</span></div>
            <div className="text-xs text-teal-600 font-medium mt-1">{bcData.score_label || 'Score'}</div>
          </div>
        )}

        {/* Einstiegsdaten */}
        {bcData?.einstiegsDaten && (
          <CollapsibleSection title="Einstiegsdaten" defaultOpen>
            <div className="space-y-1.5">
              {bcData.einstiegsDaten.branche && (
                <div><span className="font-medium text-gray-700">Branche:</span> <span className="text-gray-600">{bcData.einstiegsDaten.branche}</span></div>
              )}
              {bcData.einstiegsDaten.mitarbeiter && (
                <div><span className="font-medium text-gray-700">Mitarbeiter:</span> <span className="text-gray-600">{bcData.einstiegsDaten.mitarbeiter}</span></div>
              )}
              {bcData.einstiegsDaten.herausforderungen && bcData.einstiegsDaten.herausforderungen.length > 0 && (
                <div>
                  <span className="font-medium text-gray-700">Herausforderungen:</span>
                  <ul className="list-disc list-inside text-gray-600 mt-1 ml-1">
                    {bcData.einstiegsDaten.herausforderungen.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}
              {bcData.einstiegsDaten.details && Object.keys(bcData.einstiegsDaten.details).length > 0 && (
                <div>
                  <span className="font-medium text-gray-700">Details:</span>
                  {Object.entries(bcData.einstiegsDaten.details).map(([key, vals]) => (
                    <div key={key} className="ml-1 mt-1">
                      <span className="text-gray-500 italic">{key}:</span>
                      <ul className="list-disc list-inside text-gray-600 ml-2">
                        {vals.map((v, i) => <li key={i}>{v}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {bcData.einstiegsDaten.digitalisierung && (
                <div><span className="font-medium text-gray-700">Digitalisierung:</span> <span className="text-gray-600">{bcData.einstiegsDaten.digitalisierung}</span></div>
              )}
              {bcData.einstiegsDaten.zeitaufwand && (
                <div><span className="font-medium text-gray-700">Zeitaufwand:</span> <span className="text-gray-600">{bcData.einstiegsDaten.zeitaufwand}</span></div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Gesprächsverlauf */}
        {bcData?.gespraechsVerlauf && bcData.gespraechsVerlauf.length > 0 && (
          <CollapsibleSection title={`Gesprächsverlauf (${bcData.gespraechsVerlauf.length} Fragen)`}>
            <div className="space-y-3">
              {bcData.gespraechsVerlauf.map((entry, i) => (
                <div key={i} className="border-l-2 border-teal-300 pl-3">
                  <div className="font-medium text-gray-700">{entry.frage}</div>
                  <div className="text-gray-600 mt-0.5">{entry.antwort}</div>
                  {entry.kernthema && <div className="text-gray-400 mt-0.5 text-[10px] uppercase tracking-wide">{entry.kernthema}</div>}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Analyse-Ergebnis */}
        {bcData?.analyse && (
          <CollapsibleSection title="Analyse-Ergebnis">
            <div className="space-y-3">
              {bcData.analyse.betrieb?.zusammenfassung && (
                <div className="text-gray-600 italic">{bcData.analyse.betrieb.zusammenfassung}</div>
              )}

              {bcData.analyse.scores_nach_bereich && (
                <div>
                  <div className="font-medium text-gray-700 mb-1">Bereichs-Scores:</div>
                  {Object.entries(bcData.analyse.scores_nach_bereich).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 mb-1">
                      <span className="w-28 text-gray-600 truncate">{key}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${val.score >= 60 ? 'bg-green-500' : val.score >= 35 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${val.score}%` }}
                        />
                      </div>
                      <span className="text-gray-700 font-medium w-8 text-right">{val.score}</span>
                    </div>
                  ))}
                </div>
              )}

              {bcData.analyse.staerken && bcData.analyse.staerken.length > 0 && (
                <div>
                  <div className="font-medium text-green-700 mb-1">Stärken:</div>
                  {bcData.analyse.staerken.map((s, i) => (
                    <div key={i} className="bg-green-50 rounded p-2 mb-1">
                      <div className="font-medium text-green-800">{s.titel}</div>
                      <div className="text-gray-600">{s.beschreibung}</div>
                    </div>
                  ))}
                </div>
              )}

              {bcData.analyse.blind_spots && bcData.analyse.blind_spots.length > 0 && (
                <div>
                  <div className="font-medium text-amber-700 mb-1">Blind Spots:</div>
                  {bcData.analyse.blind_spots.map((bs, i) => (
                    <div key={i} className="bg-amber-50 rounded p-2 mb-1">
                      <div className="font-medium text-amber-800">{bs.titel} <span className="text-amber-600 font-normal">~{bs.zeitverlust_pro_woche}/Wo.</span></div>
                      <div className="text-gray-600">{bs.was_passiert_gerade}</div>
                    </div>
                  ))}
                </div>
              )}

              {bcData.analyse.top_schwachstellen && bcData.analyse.top_schwachstellen.length > 0 && (
                <div>
                  <div className="font-medium text-red-700 mb-1">Handlungsbedarf:</div>
                  {bcData.analyse.top_schwachstellen.map((sw, i) => (
                    <div key={i} className="bg-red-50 rounded p-2 mb-1">
                      <div className="font-medium text-red-800">#{sw.rang} {sw.titel}</div>
                      <div className="text-gray-600">{sw.beschreibung}</div>
                      <div className="text-red-600 font-medium mt-0.5">~{sw.zeitverlust_pro_woche}/Wo. · Risiko: {sw.geschaeftsrisiko}</div>
                    </div>
                  ))}
                </div>
              )}

              {bcData.analyse.automatisierungspotenziale && bcData.analyse.automatisierungspotenziale.length > 0 && (
                <div>
                  <div className="font-medium text-teal-700 mb-1">Automatisierung:</div>
                  {bcData.analyse.automatisierungspotenziale.map((ap, i) => (
                    <div key={i} className="bg-teal-50 rounded p-2 mb-1">
                      <div className="font-medium text-teal-800">{ap.prozess}</div>
                      <div className="text-gray-600">Aktuell: {ap.aktueller_aufwand}</div>
                      <div className="text-teal-600 font-medium">Ersparnis: ~{ap.zeitersparnis_pro_woche}/Wo.</div>
                    </div>
                  ))}
                </div>
              )}

              {bcData.analyse.gesamtfazit && (
                <div className="bg-gray-50 rounded p-2 border-l-2 border-teal-400">
                  <div className="font-medium text-gray-700 mb-1">Fazit:</div>
                  <div className="text-gray-600">{bcData.analyse.gesamtfazit}</div>
                  {bcData.analyse.naechster_schritt && (
                    <div className="text-teal-600 font-medium mt-1">{bcData.analyse.naechster_schritt}</div>
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        <Input label="Firma" value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} />
        <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as AnfrageStatus })} options={anfrageStatusOpts} />
        <Input label="Ansprechpartner" value={form.ansprechpartner} onChange={(e) => setForm({ ...form, ansprechpartner: e.target.value })} />
        <Input label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
        <Input label="Nachfassen am" type="date" value={form.nachfass_datum} onChange={(e) => setForm({ ...form, nachfass_datum: e.target.value })} />
      </div>

      <div className="p-4 border-t border-gray-200 space-y-2 flex-shrink-0">
        <Button onClick={handleSave} className="w-full">Speichern</Button>
        <button onClick={onToKunde} className="w-full py-2 text-xs text-green-600 hover:bg-green-50 rounded border border-green-200 font-medium">Gewonnen → Kunde</button>
        <button onClick={onDelete} className="w-full py-2 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200">Löschen</button>
      </div>
    </div>
  );
}

function IntegrationContent() {
  const SUPABASE_URL = 'https://fuxdkfjqzcszkxejndgw.supabase.co';
  const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ1eGRrZmpxemNzemt4ZWpuZGd3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk5MjI4NTcsImV4cCI6MjA4NTQ5ODg1N30.DmTZRcnCP2htCJ_HxEagvUWjhYCETdwo_cdXHkF1Jow';

  const cursorPrompt = `Ich möchte mein bestehendes Kontaktformular mit meinem CRM-System verbinden.

## API Details

**API Endpoint:** ${SUPABASE_URL}/rest/v1/crm_anfragen
**Methode:** POST
**Content-Type:** application/json

**Headers:**
\`\`\`javascript
{
  'Content-Type': 'application/json',
  'apikey': '${SUPABASE_ANON_KEY}',
  'Prefer': 'return=minimal'
}
\`\`\`

## Request Body Format

\`\`\`json
{
  "firma": "Wert aus dem Firmen-Feld",
  "ansprechpartner": "Wert aus dem Namen-Feld",
  "email": "Wert aus dem E-Mail-Feld",
  "telefon": "Wert aus dem Telefon-Feld (optional)",
  "position": "Wert aus dem Position-Dropdown (optional)",
  "herausforderung": "Wert aus dem Herausforderung-Dropdown (optional)",
  "quelle": "Website Kontaktformular",
  "status": "neu"
}
\`\`\`

## Meine Formularfelder

- **Name** → \`ansprechpartner\` (Pflicht)
- **Firmenname** → \`firma\` (Pflicht)
- **E-Mail-Adresse** → \`email\` (Pflicht)
- **Telefonnummer** → \`telefon\` (optional)
- **Position** (Dropdown) → \`position\` (optional)
  - EXAKTE WERTE: "Inhaber / Geschäftsführer", "Abteilungsleiter", "Marketing / HR", "Sonstige"
- **Herausforderung** (Dropdown) → \`herausforderung\` (optional)
  - EXAKTE WERTE: "Zu wenig Fachkräfte", "Zu wenig Planbarkeit (Aufträge)", "Chaos im Büro / Prozesse", "Veralteter Außenauftritt"

## Code-Beispiel

\`\`\`javascript
async function submitKontaktformular(formData) {
  try {
    const response = await fetch('${SUPABASE_URL}/rest/v1/crm_anfragen', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': '${SUPABASE_ANON_KEY}',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify({
        firma: formData.firma,
        ansprechpartner: formData.name,
        email: formData.email,
        telefon: formData.telefon || null,
        position: formData.position || null,
        herausforderung: formData.herausforderung || null,
        quelle: 'Website Kontaktformular',  // ⚠️ FEST VORGEGEBEN!
        status: 'neu'  // ⚠️ FEST VORGEGEBEN!
      })
    });
    
    if (!response.ok) {
      throw new Error('Fehler beim Senden der Anfrage');
    }
    
    return { success: true, message: 'Vielen Dank für Ihre Anfrage! Wir melden uns in Kürze.' };
    
  } catch (error) {
    console.error('Fehler:', error);
    return { success: false, error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.' };
  }
}
\`\`\`

## Was ich brauche

Bitte füge zu meinem bestehenden Kontaktformular folgende Funktionalität hinzu:

1. **Beim Absenden des Formulars:**
   - Formular-Daten sammeln
   - POST-Request an die API senden (mit den oben genannten Headers!)
   - Während des Sendens: Submit-Button deaktivieren + "Wird gesendet..." anzeigen

2. **Bei Erfolg (response.ok):**
   - Erfolgsmeldung anzeigen: "Vielen Dank für Ihre Anfrage! Wir melden uns in Kürze."
   - Formular zurücksetzen

3. **Bei Fehler:**
   - Fehlermeldung anzeigen: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut."

4. **Error Handling:**
   - Netzwerkfehler abfangen
   - API-Fehler behandeln
   - Benutzerfreundliche Meldungen auf Deutsch

## WICHTIG

- Der \`apikey\` Header ist **PFLICHT**
- Der \`Prefer: return=minimal\` Header verhindert unnötige Daten in der Response
- Alle Felder außer \`firma\`, \`ansprechpartner\` und \`email\` sind optional
- Die Werte für \`quelle\` und \`status\` sind **FEST VORGEGEBEN** und dürfen NICHT geändert werden:
  - \`quelle\` muss IMMER "Website Kontaktformular" sein
  - \`status\` muss IMMER "neu" sein
- Die Werte für \`position\` und \`herausforderung\` müssen **EXAKT** so geschrieben werden wie oben angegeben (mit korrekter Groß-/Kleinschreibung!)

Bitte integriere diese API-Anbindung in mein bestehendes Kontaktformular. Passe die Feld-IDs/Namen an mein Formular an.`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(cursorPrompt);
    toast.success('Cursor Prompt kopiert!');
  };

  return (
    <div className="space-y-4">
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
        <p className="text-sm text-neutral-700">
          Kopiere diesen Prompt und füge ihn in Cursor in deinem <strong>Website-Projekt</strong> ein.
        </p>
        <p className="text-sm text-neutral-500 mt-1">
          Alle Anfragen erscheinen dann automatisch hier unter "Anfragen".
        </p>
      </div>

      <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto">
        {cursorPrompt}
      </pre>

      <Button className="w-full" onClick={copyToClipboard}>
        Cursor Prompt kopieren
      </Button>

      <div className="bg-neutral-50 rounded-lg p-3 text-xs text-neutral-600">
        <strong>Hinweis:</strong> Die Integration nutzt direkt die Supabase REST API - keine Edge Function nötig!
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Potenzial-Check (Lead-Magnet nico-els.de) — Tabelle + Detail
//  Snapshot liegt als JSON in notizen (type: 'potenzial_check'), s. api/_lib/potenzial/analyse.ts
// ══════════════════════════════════════════════════════════════════════════

interface PotenzialCheckData {
  type: 'potenzial_check';
  check_id: string;
  website: string;
  fokus?: string | null;
  betrieb?: { firma: string; gewerk: string; ort: string | null; plz: string | null; kurz?: string; ist_handwerk?: boolean };
  auftraege?: { suchvolumen: number; cpc: number | null; ads_status: string; kosten: { von: number; bis: number }; anfragen: Record<string, { von: number; bis: number }>; keywords?: Array<{ text: string; volumen: number }> };
  personal?: { fachkraefte: number; einwohner: number; wechsler: { von: number; bis: number }; kosten: { von: number; bis: number }; bewerbungen: Record<string, { von: number; bis: number }> };
  ratgeber?: { suchvolumen: number; stufe: string };
  ki?: { verfuegbar: boolean; sichtbar: boolean; wettbewerber: string[] };
  website_befunde?: string[];
  umkreis?: { einwohner: number; gemeinden: number; groesste?: Array<{ name: string; einwohner: number; km: number }> } | null;
  erstellt_am?: string;
}

function parsePotenzialNotizen(notizen: string | null): PotenzialCheckData | null {
  if (!notizen) return null;
  try {
    const data = JSON.parse(notizen);
    if (data.type === 'potenzial_check') return data;
  } catch { /* not JSON */ }
  return null;
}

const ADS_STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  gut: { text: 'Ads lohnt sich', cls: 'bg-green-100 text-green-700' },
  knapp: { text: 'Ads knapp', cls: 'bg-yellow-100 text-yellow-700' },
  zu_wenig: { text: 'Zu wenig Suche → organisch', cls: 'bg-gray-100 text-gray-600' },
};

function PotenzialCheckTable({ anfragen, selectedId, onSelect }: {
  anfragen: CrmAnfrage[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (anfragen.length === 0) {
    return <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500">Noch keine Potenzial-Checks — sie erscheinen hier, sobald jemand auf nico-els.de seine Website prüfen lässt.</div>;
  }
  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden h-full flex flex-col">
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Betrieb</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Gewerk · Ort</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Suche/Monat</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Anfragen bei 30 €/Tag</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">KI</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Kontakt</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-4 py-3">Datum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {anfragen.map((a) => {
              const d = parsePotenzialNotizen(a.notizen);
              const anf = d?.auftraege?.anfragen?.['30'];
              const ads = d?.auftraege?.ads_status ? ADS_STATUS_LABEL[d.auftraege.ads_status] : null;
              return (
                <tr key={a.id} onClick={() => onSelect(a.id)} className={`cursor-pointer hover:bg-gray-50 ${selectedId === a.id ? 'bg-amber-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{a.firma}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[220px]">{d?.website?.replace(/^https?:\/\//, '') || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{d?.betrieb?.gewerk || '—'}<div className="text-xs text-gray-500">{[d?.betrieb?.plz, d?.betrieb?.ort].filter(Boolean).join(' ')}</div></td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {d?.auftraege ? d.auftraege.suchvolumen.toLocaleString('de-DE') : '—'}
                    {ads && <div><span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${ads.cls}`}>{ads.text}</span></div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{anf ? `${anf.von}–${anf.bis}` : '—'}</td>
                  <td className="px-4 py-3 text-sm">{d?.ki?.verfuegbar ? (d.ki.sichtbar ? <span className="text-green-700">sichtbar</span> : <span className="text-red-600">nicht sichtbar</span>) : <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{a.email ? <span className="text-green-700">E-Mail ✓</span> : <span className="text-gray-400">nur Website</span>}</td>
                  <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">{anfrageStatusLabels[a.status] || a.status}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(a.created_at).toLocaleDateString('de-DE')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Spanne({ s }: { s?: { von: number; bis: number } | null }) {
  if (!s) return <span>—</span>;
  return <span>{s.von.toLocaleString('de-DE')}–{s.bis.toLocaleString('de-DE')}</span>;
}

function PotenzialCheckDetailPanel({ anfrage, onUpdate, onDelete, onToKunde, onClose }: {
  anfrage: CrmAnfrage;
  onUpdate: (data: Partial<CrmAnfrage>) => void;
  onDelete: () => void;
  onToKunde: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    firma: anfrage.firma,
    status: anfrage.status,
    ansprechpartner: anfrage.ansprechpartner || '',
    email: anfrage.email || '',
    telefon: anfrage.telefon || '',
    nachfass_datum: anfrage.nachfass_datum || '',
  });
  useEffect(() => {
    setForm({ firma: anfrage.firma, status: anfrage.status, ansprechpartner: anfrage.ansprechpartner || '', email: anfrage.email || '', telefon: anfrage.telefon || '', nachfass_datum: anfrage.nachfass_datum || '' });
  }, [anfrage]);
  const handleSave = () => onUpdate({ firma: form.firma, status: form.status, ansprechpartner: form.ansprechpartner || null, email: form.email || null, telefon: form.telefon || null, nachfass_datum: form.nachfass_datum || null });

  const [berichtBusy, setBerichtBusy] = useState<'pdf' | 'senden' | 'followup' | null>(null);
  const d = parsePotenzialNotizen(anfrage.notizen);
  const qc = useQueryClient();
  const { data: admin } = useQuery({
    queryKey: ['potenzial-admin', d?.check_id],
    queryFn: () => getPotenzialAdmin(d!.check_id),
    enabled: !!d?.check_id,
    staleTime: 30_000,
  });
  const fu = admin?.check;
  const followupAktiv = !!fu && fu.followup_opt_in && !fu.followup_gestoppt_at && fu.followup_stufe < 3;
  const followupLabel = !fu ? '…'
    : fu.followup_stufe >= 3 ? 'Sequenz abgeschlossen (3/3)'
    : fu.followup_gestoppt_at ? `gestoppt (${fu.followup_stopp_grund === 'abgemeldet' ? 'abgemeldet' : fu.followup_stopp_grund?.startsWith('crm_status') ? 'CRM-Status' : fu.followup_stopp_grund === 'lms' ? 'manuell' : fu.followup_stopp_grund || '–'}) · ${fu.followup_stufe}/3 gesendet`
    : !fu.followup_opt_in ? 'kein Opt-in'
    : fu.bericht_status !== 'gesendet' ? 'wartet auf Bericht'
    : `${fu.followup_stufe}/3 gesendet · nächste ${fu.followup_naechste_at ? new Date(fu.followup_naechste_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}`;
  const anfrageStatusOpts = Object.entries(anfrageStatusLabels).map(([k, v]) => ({ value: k, label: v }));
  const ergebnisUrl = d?.check_id ? `https://nico-els.de/potenzial-check/?id=${d.check_id}` : null;

  return (
    <div className="bg-white border border-gray-200 rounded h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <span className="text-sm font-medium text-gray-900">Potenzial-Check</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3 text-sm">
        {d?.betrieb && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="font-semibold text-gray-900">{d.betrieb.firma}</div>
            <div className="text-xs text-gray-600">{d.betrieb.gewerk} · {[d.betrieb.plz, d.betrieb.ort].filter(Boolean).join(' ') || 'Ort unbekannt'}{d.betrieb.ist_handwerk === false ? ' · kein Handwerksbetrieb' : ''}</div>
            {d.betrieb.kurz && <div className="text-xs text-gray-500 mt-1 italic">{d.betrieb.kurz}</div>}
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <a href={d.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Website öffnen</a>
              {ergebnisUrl && <a href={ergebnisUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Ergebnisseite</a>}
              {d.fokus && <span className="text-gray-500">Einstieg: {d.fokus}</span>}
            </div>
          </div>
        )}

        {d?.check_id && (
          <div className="border border-gray-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">Vollständiger Bericht (PDF)</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={berichtBusy !== null}
                onClick={async () => { setBerichtBusy('pdf'); try { await openPotenzialBerichtPdf(d.check_id!); } catch (e: any) { toast.error(e?.message || 'PDF fehlgeschlagen'); } finally { setBerichtBusy(null); } }}
                className="px-2.5 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >{berichtBusy === 'pdf' ? 'Wird erzeugt …' : 'PDF öffnen'}</button>
              <button
                type="button"
                disabled={berichtBusy !== null}
                onClick={async () => {
                  const ziel = form.email || anfrage.email || '';
                  if (!ziel) { toast.error('Erst eine E-Mail-Adresse eintragen und speichern.'); return; }
                  if (!confirm(`Bericht an ${ziel} senden?`)) return;
                  setBerichtBusy('senden');
                  try { const r = await sendePotenzialBericht(d.check_id!, ziel); if (r.gesendet) toast.success(`Bericht an ${ziel} gesendet`); else toast.error(r.grund || 'Versand fehlgeschlagen'); }
                  catch (e: any) { toast.error(e?.message || 'Versand fehlgeschlagen'); }
                  finally { setBerichtBusy(null); }
                }}
                className="px-2.5 py-1.5 text-xs rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >{berichtBusy === 'senden' ? 'Wird gesendet …' : 'Bericht per Mail senden'}</button>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              {fu?.bericht_status === 'gesendet' ? `Gesendet ${fu.bericht_gesendet_at ? new Date(fu.bericht_gesendet_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''} an ${fu.email}` : fu?.bericht_status === 'angefordert' ? `Angefordert (${fu.email}) — Versand läuft${fu.bericht_fehler ? ` · letzter Fehler: ${fu.bericht_fehler}` : ''}` : 'Noch nicht angefordert (kein E-Mail-Gate).'}
            </p>
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-gray-700">Follow-up-Mails (Tag 2 / 6 / 12)</div>
                <div className="text-[11px] text-gray-500">{followupLabel}</div>
              </div>
              <button
                type="button"
                disabled={berichtBusy !== null || !fu || !fu.email || fu.followup_stufe >= 3}
                onClick={async () => {
                  setBerichtBusy('followup');
                  try { await setzePotenzialFollowup(d.check_id!, !followupAktiv); await qc.invalidateQueries({ queryKey: ['potenzial-admin', d.check_id] }); toast.success(!followupAktiv ? 'Follow-up aktiviert' : 'Follow-up gestoppt'); }
                  catch (e: any) { toast.error(e?.message || 'Fehlgeschlagen'); }
                  finally { setBerichtBusy(null); }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 ${followupAktiv ? 'bg-green-500' : 'bg-gray-300'}`}
                aria-label="Follow-up an/aus"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${followupAktiv ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {admin?.mails && admin.mails.length > 0 && (
              <ul className="mt-2 text-[11px] text-gray-500 space-y-0.5">
                {admin.mails.map((m, i) => <li key={i}>{new Date(m.gesendet_at).toLocaleDateString('de-DE')} · {m.art === 'bericht' ? 'Bericht' : m.art.replace('followup_', 'Follow-up ')}{m.fehler ? ` · Fehler: ${m.fehler}` : ''}</li>)}
              </ul>
            )}
          </div>
        )}

        {d?.auftraege && (
          <CollapsibleSection title="Aufträge über Google" defaultOpen>
            <div className="space-y-1.5">
              <div><span className="font-medium text-gray-700">Suchvolumen im Umkreis:</span> <span className="text-gray-600">{d.auftraege.suchvolumen.toLocaleString('de-DE')} / Monat</span> {d.auftraege.cpc != null && <span className="text-gray-400">· Ø CPC {d.auftraege.cpc.toFixed(2)} €</span>}</div>
              <div><span className="font-medium text-gray-700">Kosten je Anfrage:</span> <span className="text-gray-600"><Spanne s={d.auftraege.kosten} /> €</span></div>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {['20', '30', '50'].map((b) => (
                  <div key={b} className="bg-gray-50 rounded p-2 text-center">
                    <div className="text-[10px] text-gray-500">{b} €/Tag</div>
                    <div className="font-semibold text-gray-900"><Spanne s={d.auftraege!.anfragen?.[b]} /></div>
                    <div className="text-[10px] text-gray-500">Anfragen/Monat</div>
                  </div>
                ))}
              </div>
              {d.auftraege.keywords && d.auftraege.keywords.length > 0 && (
                <div className="mt-1">
                  <div className="font-medium text-gray-700">Top-Suchbegriffe:</div>
                  <ul className="text-gray-600 mt-0.5">
                    {d.auftraege.keywords.slice(0, 6).map((k, i) => <li key={i} className="flex justify-between"><span>{k.text}</span><span className="text-gray-400">{k.volumen}</span></li>)}
                  </ul>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {d?.website_befunde && d.website_befunde.length > 0 && (
          <CollapsibleSection title={`Website-Befunde (${d.website_befunde.length})`} defaultOpen>
            <ul className="list-disc list-inside text-gray-600 space-y-1">{d.website_befunde.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </CollapsibleSection>
        )}

        {d?.personal && (
          <CollapsibleSection title="Mitarbeitergewinnung">
            <div className="space-y-1.5">
              <div><span className="font-medium text-gray-700">Einwohner im Umkreis:</span> <span className="text-gray-600">{d.personal.einwohner.toLocaleString('de-DE')}</span></div>
              <div><span className="font-medium text-gray-700">Fachkräfte des Gewerks:</span> <span className="text-gray-600">~{d.personal.fachkraefte.toLocaleString('de-DE')}</span> <span className="text-gray-400">· Wechsler/Jahr <Spanne s={d.personal.wechsler} /></span></div>
              <div><span className="font-medium text-gray-700">Kosten je Bewerbung:</span> <span className="text-gray-600"><Spanne s={d.personal.kosten} /> €</span></div>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {['20', '30', '50'].map((b) => (
                  <div key={b} className="bg-gray-50 rounded p-2 text-center">
                    <div className="text-[10px] text-gray-500">{b} €/Tag</div>
                    <div className="font-semibold text-gray-900"><Spanne s={d.personal!.bewerbungen?.[b]} /></div>
                    <div className="text-[10px] text-gray-500">Bewerbungen/Monat</div>
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>
        )}

        {(d?.ki || d?.ratgeber) && (
          <CollapsibleSection title="KI-Sichtbarkeit & Ratgeber-Nachfrage">
            <div className="space-y-1.5">
              {d?.ki && (
                <div><span className="font-medium text-gray-700">In KI-Antworten:</span> {d.ki.verfuegbar ? (d.ki.sichtbar ? <span className="text-green-700">wird empfohlen</span> : <span className="text-red-600">kommt nicht vor</span>) : <span className="text-gray-400">nicht geprüft</span>}
                  {d.ki.wettbewerber?.length > 0 && <div className="text-xs text-gray-500 mt-0.5">Genannt: {d.ki.wettbewerber.join(', ')}</div>}
                </div>
              )}
              {d?.ratgeber && <div><span className="font-medium text-gray-700">Ratgeber-Nachfrage:</span> <span className="text-gray-600">{d.ratgeber.suchvolumen.toLocaleString('de-DE')} / Monat ({d.ratgeber.stufe})</span></div>}
            </div>
          </CollapsibleSection>
        )}

        <div className="border-t border-gray-100 pt-3 space-y-2">
          <div><label className="text-xs text-gray-500">Status</label><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as AnfrageStatus })} options={anfrageStatusOpts} /></div>
          <div><label className="text-xs text-gray-500">Ansprechpartner</label><input value={form.ansprechpartner} onChange={(e) => setForm({ ...form, ansprechpartner: e.target.value })} className="w-full border border-gray-200 rounded px-2 py-1 text-sm" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-gray-500">E-Mail</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded px-2 py-1 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Telefon</label><input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="w-full border border-gray-200 rounded px-2 py-1 text-sm" /></div>
          </div>
          <div><label className="text-xs text-gray-500">Nachfassen am</label><input type="date" value={form.nachfass_datum} onChange={(e) => setForm({ ...form, nachfass_datum: e.target.value })} className="w-full border border-gray-200 rounded px-2 py-1 text-sm" /></div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-2 flex-shrink-0">
        <Button size="sm" onClick={handleSave}>Speichern</Button>
        <Button size="sm" variant="secondary" onClick={onToKunde}>Gewonnen → Kunde</Button>
        <button onClick={onDelete} className="ml-auto text-xs text-red-500 hover:text-red-700">Löschen</button>
      </div>
    </div>
  );
}
